import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import ToolShell from "./ToolShell";
import { Panel, NumField, Stat } from "./ToolUI";
import { num, money, saveResult } from "./toolkit";

const DEFAULTS = { planned: "120", hours: "8", events: "6", operators: "6", labor: "38", units: "240", margin: "2.75", overhead: "185", restart: "150", weeks: "50" };
const COLORS = ["#3b82f6", "#ea580c", "#a1a1aa", "#10b981"];

const compute = (f) => {
  const laborHr = num(f.operators) * num(f.labor);
  const marginHr = num(f.units) * num(f.margin);
  const overheadHr = num(f.overhead);
  const hourly = laborHr + marginHr + overheadHr;
  const restartWk = num(f.events) * num(f.restart);
  const weekly = hourly * num(f.hours) + restartWk;
  const weeks = num(f.weeks);
  const annual = weekly * weeks;
  const lossPct = num(f.planned) ? (num(f.hours) / num(f.planned)) * 100 : 0;
  const breakdown = [
    { name: "Lost margin", value: marginHr * num(f.hours) * weeks },
    { name: "Idle labor", value: laborHr * num(f.hours) * weeks },
    { name: "Overhead", value: overheadHr * num(f.hours) * weeks },
    { name: "Restart / scrap", value: restartWk * weeks },
  ];
  return { hourly, weekly, annual, lossPct, breakdown, perEvent: num(f.events) ? weekly / num(f.events) : 0 };
};

export default function DowntimeCost() {
  const [f, setF] = useState(DEFAULTS);
  const [touched, setTouched] = useState(false);
  const set = (k) => (v) => { setTouched(true); setF((s) => ({ ...s, [k]: v })); };
  const r = useMemo(() => compute(f), [f]);
  const nav = useNavigate();
  const toOEE = () => { saveResult("downtime", { annual: r.annual, hourly: r.hourly, lossPct: r.lossPct }); nav("/tools/oee-calculator?from=downtime"); };

  useEffect(() => {
    if (touched) saveResult("downtime", { annual: r.annual, hourly: r.hourly, lossPct: r.lossPct });
  }, [touched, r.annual, r.hourly, r.lossPct]);

  const share = {
    slug: "downtime-cost", tool: "Cost of Downtime", scoreText: `${money(r.annual)}/yr`, buttonLabel: "Share my downtime cost",
    label: `${money(r.hourly)} per hour · ${r.lossPct.toFixed(1)}% of planned time lost`,
    bar: { value: Math.max(0, 100 - r.lossPct * 4), target: 80, targetLabel: "Target: <5% downtime" },
    tagline: "Winning those hours back with RuleAI",
    caption: `Unplanned downtime is costing our line ${money(r.annual)} a year (${money(r.hourly)}/hr). Winning it back with RuleAI.`,
  };

  return (
    <ToolShell toolKey="downtime" overline="Free tool · Downtime" title="Downtime Cost Calculator"
      intro="Most plants underestimate downtime by 3–5×. Add up lost margin, idle labor, overhead and restart scrap to see what one hour of unplanned downtime really costs."
      share={share} results={{ annual: r.annual, hourly: r.hourly, weekly: r.weekly, lossPct: r.lossPct, inputs: f }}>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6">
        <Panel overline="01 / Inputs" title="Your line" testid="downtime-inputs">
          <div className="grid sm:grid-cols-2 gap-4">
            <NumField label="Planned production" suffix="hrs / wk" value={f.planned} onChange={set("planned")} testid="downtime-planned" />
            <NumField label="Unplanned downtime" suffix="hrs / wk" value={f.hours} onChange={set("hours")} testid="downtime-hours" />
            <NumField label="Downtime events" suffix="/ wk" value={f.events} onChange={set("events")} testid="downtime-events" />
            <NumField label="Operators idled" suffix="people" value={f.operators} onChange={set("operators")} testid="downtime-operators" />
            <NumField label="Burdened labor rate" suffix="$ / hr" value={f.labor} onChange={set("labor")} testid="downtime-labor" />
            <NumField label="Output lost" suffix="units / hr" value={f.units} onChange={set("units")} testid="downtime-units" />
            <NumField label="Contribution margin" suffix="$ / unit" value={f.margin} onChange={set("margin")} testid="downtime-margin" />
            <NumField label="Fixed overhead" suffix="$ / hr" value={f.overhead} onChange={set("overhead")} testid="downtime-overhead" />
            <NumField label="Restart & scrap" suffix="$ / event" value={f.restart} onChange={set("restart")} testid="downtime-restart" />
            <NumField label="Operating weeks" suffix="/ yr" value={f.weeks} onChange={set("weeks")} testid="downtime-weeks" />
          </div>
        </Panel>
        <Panel overline="02 / Results" title="What downtime costs you" testid="downtime-results">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Stat big label="Annual loss" value={money(r.annual)} tone="text-accent" sub={`${r.lossPct.toFixed(1)}% of planned time lost`} testid="downtime-annual" /></div>
            <Stat label="Per hour" value={money(r.hourly)} testid="downtime-hourly" />
            <Stat label="Per week" value={money(r.weekly)} testid="downtime-weekly" />
            <Stat label="Per event" value={money(r.perEvent)} testid="downtime-per-event" />
            <Stat label="Cut 25% saves" value={money(r.annual * 0.25)} sub="per year" tone="text-emerald-400" testid="downtime-savings" />
          </div>
          <div className="h-60 mt-5">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={r.breakdown} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} stroke="#0a0a0b">
                  {r.breakdown.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} itemStyle={{ color: "#fafafa" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <Button onClick={toOEE} variant="outline" className="rounded-sm h-11 w-full mt-4 border-primary/50 hover:border-primary no-print" data-testid="downtime-to-oee-btn">
            <Gauge className="w-4 h-4 mr-2" /> Use in OEE Calculator ({r.lossPct.toFixed(1)}% downtime) <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Panel>
      </div>
    </ToolShell>
  );
}
